import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { createDataSelectInfiniteQueryOptions } from "../actions/data-select.actions";
import {
  EDataSelectSearchMode,
  EDataSelectSourceType,
  IDataSelectApiSourceApiSearch,
  IDataSelectApiSourceLocalSearch,
  IDataSelectApiSourceNoSearch,
  IDataSelectOption,
  IDataSelectStaticSource,
} from "../dataselector.interface";
import { useDebouncedValue } from "./useDebouceValue";

export interface IUseDataSelectReturn {
  options: IDataSelectOption[];
  selectedOption: IDataSelectOption | null;
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  errorMessage: string | null;
  loadMore: () => void;
}

const normalizeApiItemsToOptions = (items: Record<string, unknown>[], valueKey: string, labelKey: string): IDataSelectOption[] => {
  const seen = new Set<string>();

  return items.reduce<IDataSelectOption[]>((accumulator, item) => {
    const uid = String(item[valueKey] ?? "");
    const label = String(item[labelKey] ?? uid);

    if (!uid || seen.has(uid)) {
      return accumulator;
    }

    seen.add(uid);

    accumulator.push({
      uid,
      label,
      raw: item,
    });

    return accumulator;
  }, []);
};

export const useDataSelect = (
  source: IDataSelectStaticSource | IDataSelectApiSourceLocalSearch | IDataSelectApiSourceApiSearch | IDataSelectApiSourceNoSearch,
  value: string | null,
): IUseDataSelectReturn => {
  const [inputValue, setInputValue] = useState<string>("");

  const debouncedInputValue = useDebouncedValue(inputValue, 350);

  const apiSearchTerm = useMemo(() => {
    if (source.type !== EDataSelectSourceType.API) {
      return "";
    }

    if (source.searchMode !== EDataSelectSearchMode.API) {
      return "";
    }

    const normalizedValue = debouncedInputValue.trim();
    const minSearchLength = source.minSearchLength ?? 0;

    if (!normalizedValue || normalizedValue.length < minSearchLength) {
      return "";
    }

    return normalizedValue;
  }, [debouncedInputValue, source]);

  const query = useInfiniteQuery(
    source.type === EDataSelectSourceType.API
      ? createDataSelectInfiniteQueryOptions({
          url: source.url,
          bearerToken: source.bearerToken,
          pageSize: source.pageSize,
          queryParams: source.queryParams,
          searchTerm: apiSearchTerm,
          searchParamKey: source.searchMode === EDataSelectSearchMode.API ? source.searchParamKey : undefined,
          enabled: source.enabled ?? true,
        })
      : createDataSelectInfiniteQueryOptions({
          url: "",
          enabled: false,
        }),
  );

  const normalizedApiOptions = useMemo<IDataSelectOption[]>(() => {
    if (source.type !== EDataSelectSourceType.API) {
      return [];
    }

    const valueKey = source.valueKey ?? "uid";
    const labelKey = source.labelKey ?? "name";
    const items = query.data?.pages.flatMap((page) => page.items ?? []) ?? [];

    return normalizeApiItemsToOptions(items, valueKey, labelKey);
  }, [query.data?.pages, source]);

  const options = useMemo<IDataSelectOption[]>(() => {
    if (source.type === EDataSelectSourceType.STATIC) {
      if (!inputValue.trim()) {
        return source.options;
      }

      const normalizedSearch = inputValue.trim().toLowerCase();

      return source.options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
    }

    if (source.searchMode === EDataSelectSearchMode.LOCAL && inputValue.trim()) {
      const normalizedSearch = inputValue.trim().toLowerCase();

      return normalizedApiOptions.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
    }

    return normalizedApiOptions;
  }, [inputValue, normalizedApiOptions, source]);

  const selectedOption = useMemo<IDataSelectOption | null>(() => {
    if (!value) {
      return null;
    }

    if (source.type === EDataSelectSourceType.STATIC) {
      return source.options.find((option) => option.uid === value) ?? null;
    }

    return normalizedApiOptions.find((option) => option.uid === value) ?? null;
  }, [normalizedApiOptions, source, value]);

  return {
    options,
    selectedOption,
    inputValue,
    setInputValue,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    errorMessage: query.error instanceof Error ? query.error.message : null,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
  };
};
